import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

/**
 * Public Terms of Service page — clean reading layout.
 */
const TermsOfServicePage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200">
      <div className="sticky top-0 z-10 bg-slate-900/90 backdrop-blur border-b border-slate-800">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-slate-300 hover:text-white"
            data-testid="terms-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <h1 className="text-white font-semibold">Terms of Service</h1>
        </div>
      </div>

      <article
        className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12 leading-relaxed"
        data-testid="terms-of-service-content"
      >
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
          Mobile Messaging Terms of Service
        </h1>
        <p className="text-sm text-slate-400 mb-8">
          By initiating a text message to our reservation line at (702) 546-6394, or by submitting an online reservation request that generates a text message from your mobile device, you agree to these Mobile Messaging Terms.
        </p>

        <section className="mb-8">
          <h2 className="text-xl sm:text-2xl font-bold text-red-500 mb-3">
            1. Program Description &amp; Opt-In Process
          </h2>
          <p className="mb-4">
            Fin &amp; Feathers provides an automated and manual text messaging service to assist customers in booking, pre-qualifying, and confirming restaurant reservations.
          </p>
          <p className="mb-4">
            By opting in, you provide your explicit consent to receive automated and manual text messages from Fin &amp; Feathers regarding your booking request, including party size screening questions, deposit payment link requests, and final reservation status alerts. Consent is a requirement to utilize the text-booking service, but it is not a general condition of purchase at our physical restaurant location.
          </p>
          <p className="mb-3">
            Customers can opt-in to our services through three distinct pathways:
          </p>
          <ul className="list-disc list-outside pl-6 space-y-3 mb-4">
            <li>
              <strong className="text-white">Web-to-Text Form Submission (Reservations)</strong>
            </li>
            <li>
              <strong className="text-white">Direct Text Interaction (Reservations)</strong>
              <p className="mt-2 text-slate-300">
                By opting in, you provide your explicit consent to receive automated and manual text messages from Fin &amp; Feathers regarding your booking request, including party size screening questions, deposit payment link requests, and final reservation status alerts. Consent is a requirement to utilize the text-booking service, but it is not a general condition of purchase at our physical restaurant location.
              </p>
            </li>
            <li>
              <strong className="text-white">Loyalty Program Web Form Submission</strong>
              <p className="mt-2 text-slate-300">
                By entering your mobile phone number and checking the consent box on our homepage &quot;Join Our Loyalty Program&quot; form, you provide your explicit consent to receive recurring, automated promotional marketing text messages and rewards alerts from Fin &amp; Feathers. Consent to receive promotional marketing texts is entirely voluntary and is not a condition of purchase for dining at our restaurant.
              </p>
            </li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl sm:text-2xl font-bold text-red-500 mb-3">2. Message and Data Rates</h2>
          <p>
            Message and data rates may apply for any messages sent to you from us and to us from you. You can expect to receive between 3 to 6 messages per reservation request to successfully complete the screening and confirmation process. If you have any questions about your text plan or data plan, it is best to contact your wireless provider. For the Loyalty Program, message frequency varies, but you can typically expect up to 4 promotional messages per month.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl sm:text-2xl font-bold text-red-500 mb-3">3. How to Opt-Out</h2>
          <p>
            You may opt-out of mobile communications at any time. To stop receiving text messages from Fin &amp; Feathers, reply &quot;STOP&quot; to (702) 546-6394. Opting out via &quot;STOP&quot; will unsubscribe you from all text communications from us, including both reservation updates and loyalty rewards alerts.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl sm:text-2xl font-bold text-red-500 mb-3">4. Help and Support</h2>
          <p>
            If you are experiencing issues with the messaging program, you can reply with the keyword &quot;HELP&quot; for more assistance, or you can contact our restaurant directly by calling our host stand.
          </p>
        </section>

        <div className="pt-6 border-t border-slate-800 text-sm text-slate-500">
          See also our{' '}
          <button
            onClick={() => navigate('/privacy-policy')}
            className="text-red-400 hover:text-red-300 underline"
            data-testid="terms-link-privacy"
          >
            Privacy Policy
          </button>
          .
        </div>
      </article>
    </div>
  );
};

export default TermsOfServicePage;
